import { useCallback, useContext, useMemo } from 'react'
import { Gradient, Range } from 'types'
import { iconComponent, RangeSlider } from 'ui'
import { UgeArtboardContext } from '../../../contexts/artboard'
import { UgeLayersContext } from '../../../contexts/layers'
import useSourcePreviewUrl from '../../../hooks/use-source-preview-url'
import useTextStyleCss from '../../../hooks/use-text-style-css'
import { CaptionLayerProps, ImageLayerProps, Layer } from '../../../types'
import { Duration, Preview, Title } from './style'

type Props = {
    color?: string
    gradient?: Gradient
    layer: Layer
}

const LayersEditorLayerHeader = ({ gradient, layer: { id, name, props, type } }: Props) => {
    const { duration } = useContext(UgeArtboardContext)
    const { updateLayer } = useContext(UgeLayersContext)
    const previewUrl = useSourcePreviewUrl((props as ImageLayerProps).source)
    const titleStyle = useTextStyleCss((props as CaptionLayerProps).textStyle)
    const { timing } = props

    const timingRange = useMemo(() => {
        if (!timing) return

        return { end: timing.end / duration, start: timing.start / duration }
    }, [duration, timing?.start, timing?.end])

    const timingLabel = useMemo(() => {
        if (!timing) {
            return duration
        }

        return Math.round((timing.end - timing.start) * 100) / 100
    }, [duration, timing?.start, timing?.end])

    const Icon = useMemo(() => {
        switch (type) {
            case 'caption':
                return iconComponent('text')
            case 'image':
                return iconComponent('sticker')
        }
    }, [type])

    const onTimingChange = useCallback(
        (range: Range) => {
            updateLayer(id, {
                timing: {
                    start: range.start * duration,
                    end: range.end * duration,
                },
            })
        },
        [duration]
    )

    return (
        <RangeSlider
            disabled={!duration}
            gradient={gradient}
            range={timingRange}
            size="medium"
            onChange={onTimingChange}
        >
            {previewUrl && <Preview src={previewUrl} alt="" />}
            <Title style={titleStyle}>{name || (props as CaptionLayerProps).text}</Title>
            <Icon height={18} style={{ flexShrink: 0 }} />
            <Duration>
                {timingLabel}s <span>/ {duration}s</span>
            </Duration>
        </RangeSlider>
    )
}

export default LayersEditorLayerHeader
